<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'

interface Props {
  visible: boolean
  title: string
  fullscreenMobile?: boolean
  width?: string
  modal?: boolean
  closable?: boolean
  closeOnEscape?: boolean
  /**
   * Lo que pone el botón de aceptar.
   *
   * Por defecto vale «Confirmar», que no dice nada. En el aviso de guardar sobre una
   * factura emitida, «Confirmar» deja al usuario sin decidir: «Guardar igualmente» sí
   * dice qué va a pasar.
   */
  confirmLabel?: string
}

/**
 * Los booleanos llevan valor por defecto explícito y no se dejan en `undefined`.
 *
 * Es la causa de que **ningún diálogo de la aplicación tuviera botón de cerrar ni
 * botón de cancelar**: con `closable?: boolean` a secas, Vue aplica su regla de
 * casting y un prop booleano ausente vale `false`, no `undefined`. Entonces
 * `props.closable ?? true` devolvía `false` —porque `??` solo sustituye `null` y
 * `undefined`— y `props.closable !== false` también, así que ni la X ni «Cancelar»
 * se llegaban a pintar. Se veía solo el botón de confirmar, y el diálogo se quedaba
 * abierto sin forma de salir salvo cancelar la operación.
 *
 * Con `withDefaults` el valor ausente es el que se quiere y no hay que ir tachándolo con
 * `??` en cada sitio.
 *
 * También se quitó el manejador de la tecla Escape que tenía aquí. PrimeVue ya la
 * resuelve con `closeOnEscape`, así que el nuestro era un segundo cierre: `cancel`
 * llegaba dos veces por pulsación. Y el prop `dismissibleMask` que lo alimentaba no
 * existe en el `Dialog` de PrimeVue 4 —se comprobó en `node_modules`—, así que
 * también caía en el diálogo como un atributo suelto que nadie leía.
 */
const props = withDefaults(defineProps<Props>(), {
  fullscreenMobile: false,
  modal: true,
  closable: true,
  closeOnEscape: true,
})

/**
 * Nombre explícito para no confundirse con el `Dialog` de PrimeVue.
 *
 * Este componente envuelve al de PrimeVue, así que sin nombre propio los dos se
 * llaman `Dialog`. En los tests, `findAllComponents({ name: 'Dialog' })` devolvía
 * seis componentes en vez de tres, y confirmar en el índice equivocado disparaba
 * la acción de otro diálogo sin que nada fallara.
 */
defineOptions({ name: 'AppDialog' })

const emit = defineEmits<{
  'update:visible': [visible: boolean]
  confirm: []
  cancel: []
}>()

const isMobile = ref(false)
const isFullscreen = computed(() => props.fullscreenMobile && isMobile.value)

function checkMobile(): void {
  isMobile.value = window.innerWidth < 768
}

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

watch(() => props.visible, (val) => {
  visibleProxy.value = val
  if (val) {
    checkMobile()
  }
})

/**
 * Proxy de `visible` para el `v-model` de PrimeVue.
 *
 * Escribir directamente en `props.visible` mutaría el prop del padre, que Vue
 * prohíbe. El proxy escribe hacia arriba con `update:visible` y deja que el
 * padre decida.
 */
const visibleProxy = ref(props.visible)

watch(() => props.visible, (val) => {
  visibleProxy.value = val
})

watch(visibleProxy, (val) => {
  emit('update:visible', val)
})

/**
 * Cierra el diálogo.
 *
 * Emite las dos cosas: `update:visible` para el `v-model`, y `cancel` para que los
 * `@cancel` que hay en las pantallas sirvan de algo. Antes `cancel` estaba declarado
 * pero no se emitía nunca, y todos esos manejadores eran código muerto: cerraban
 * porque el `v-model` ya venía abajo, no porque ellos lo pidieran.
 */
function close(): void {
  console.log('[AppDialog] close() called, emitting update:visible')
  emit('update:visible', false)
  console.log('[AppDialog] update:visible emitted')
  emit('cancel')
}

// Exponer el método close para que el padre pueda cerrar el diálogo programáticamente
defineExpose({
  close,
})

function confirm(): void {
  emit('confirm')
}

</script>

<template>
  <Dialog
    v-model:visible="visibleProxy"
    :header="props.title"
    :modal="props.modal ?? true"
    :style="{ width: props.width ?? (isFullscreen ? '100vw' : '500px'), maxWidth: isFullscreen ? '100vw' : '90vw' }"
    :closable="props.closable"
    :close-on-escape="props.closeOnEscape"
    :full-screen="isFullscreen"
    :show-header="!isFullscreen"
    :base-z-index="300"
    class="p-dialog-custom"
    @hide="close"
  >
    <template
      v-if="isFullscreen"
      #header
    >
      <div class="flex items-center justify-between w-full px-4 py-3 border-b border-surface-200 dark:border-surface-700">
        <h2 class="text-lg font-semibold text-surface-900 dark:text-surface-100">
          {{ props.title }}
        </h2>
        <Button
          icon="pi pi-times"
          severity="secondary"
          text
          size="small"
          aria-label="Cerrar"
          class="p-button-rounded"
          @click="close"
        />
      </div>
    </template>

    <slot />

    <template #footer>
      <div class="flex justify-end gap-2 w-full">
        <slot name="footer">
          <Button
            v-if="props.closable !== false"
            label="Cancelar"
            severity="secondary"
            @click="close"
          />
          <Button
            :label="props.confirmLabel ?? 'Confirmar'"
            @click="confirm"
          />
        </slot>
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
/* Fullscreen mobile optimizations */
:deep(.p-dialog.p-dialog-fullscreen) {
  @media (max-width: 767px) {
    border-radius: 0;
    max-height: 100vh;
  }
}

/* Asegurar que el contenido sea scrolleable en móvil */
:deep(.p-dialog-content) {
  @media (max-width: 767px) {
    max-height: calc(100vh - 120px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
}

/* Focus visible para accesibilidad */
:deep(.p-dialog:focus-within) {
  outline: 2px solid var(--color-primary-500);
  outline-offset: 2px;
}

/* Animación suave */
:deep(.p-dialog-enter-active, .p-dialog-leave-active) {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

:deep(.p-dialog-enter-from, .p-dialog-leave-to) {
  opacity: 0;
  transform: scale(0.95);
}
</style>